import includes from 'lodash/includes';
import filter from 'lodash/filter';
import isUndefined from 'lodash/isUndefined';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import uuidV4 from 'uuid/v4';
import IconTimes from '@spothero/icons/times';
import DOMUtils from '@spothero/utils/dom';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import withConfig from 'config/withConfig';
import {
    getAmenities,
    getReservationTypes,
    getPriceRange,
} from 'filters/filters-selectors';
import {resetFilters, updateFilters} from 'filters/filters-actions';
import {toggleFiltersView} from '../search-actions';
import MonthlyFilters from 'filters/MonthlyFilters';
import AmenitiesFilters from 'filters/AmenitiesFilters';
import PriceRange from 'filters/PriceRange';

class SpotListFilters extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        isMonthly: PropTypes.bool,
        isVisible: PropTypes.bool,
        isFiltered: PropTypes.bool,
        amenities: PropTypes.object,
        activeAmenities: PropTypes.array,
        reservationTypes: PropTypes.object,
        activeReservationType: PropTypes.string,
        price: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
        priceLow: PropTypes.number,
        priceHigh: PropTypes.number,
        highestPrice: PropTypes.number,
        onCloseClick: PropTypes.func.isRequired,
        clearFilters: PropTypes.func.isRequired,
        setFilters: PropTypes.func.isRequired,
    };
    static defaultProps = {
        highestPrice: 0,
    };

    constructor(props) {
        super(props);

        const {highestPrice, isMonthly} = props;

        this.state = {
            key: uuidV4(),
            highestPrice,
            prevIsMonthly: isMonthly,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            priceHigh,
            isMonthly,
            highestPrice: nextHighestPrice,
        } = nextProps;
        const {highestPrice, prevIsMonthly} = prevState;
        const newState = {
            highestPrice,
        };

        // set the highest possible price based on what prices have already been shown
        // so that the price slider knows what the possible high is
        if (priceHigh > newState.highestPrice) {
            newState.highestPrice = priceHigh;
        }

        if (nextHighestPrice > newState.highestPrice) {
            newState.highestPrice = nextHighestPrice;
        }

        if (isMonthly !== prevIsMonthly) {
            newState.highestPrice = 0;
            newState.prevIsMonthly = isMonthly;
        }

        return newState.highestPrice !== highestPrice ? newState : null;
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.isVisible !== this.props.isVisible) {
            const pageContainer = DOMUtils.el('.search-page');

            DOMUtils.toggleClass(pageContainer, 'search-filters-open');
        }
    }

    _onAmenityChange = (amenity, checked) => {
        const {activeAmenities, setFilters} = this.props;
        const invertedAmenity = includes(amenity, 'no-')
            ? amenity.replace('no-', '')
            : `no-${amenity}`;
        let newActiveAmenities = [...activeAmenities];

        if (checked) {
            newActiveAmenities.push(amenity);
            newActiveAmenities = filter(newActiveAmenities, item => {
                return item !== invertedAmenity;
            });
        } else {
            newActiveAmenities = filter(newActiveAmenities, item => {
                return item !== amenity;
            });
        }

        setFilters({
            activeAmenities: newActiveAmenities,
        });
    };

    _onReservationTypeChange = ({value}) => {
        this.props.setFilters({
            activeReservationType: value,
        });
    };

    _onPriceRangeChange = value => {
        this.props.setFilters({
            price: value * 100,
        });
    };

    _onResetFiltersClick = () => {
        this.props.clearFilters();

        this.setState({
            key: uuidV4(),
        });
    };

    render() {
        const {
            isMobile,
            onCloseClick,
            isMonthly,
            isFiltered,
            amenities,
            reservationTypes,
            activeReservationType,
            activeAmenities,
            price,
            priceLow,
            priceHigh,
        } = this.props;
        const {highestPrice, key} = this.state;

        return (
            <div key={key} className="SpotListFilters search-filters">
                {!isMobile && (
                    <Button
                        className="SpotListFilters-close-button"
                        onClick={onCloseClick}
                    >
                        <IconTimes className="SpotListFilters-close-icon" />
                    </Button>
                )}
                {isFiltered && (
                    <TextButton
                        className="SpotListFilters-reset-button"
                        onClick={this._onResetFiltersClick}
                    >
                        Reset All
                    </TextButton>
                )}
                {!isMonthly && (
                    <AmenitiesFilters
                        amenities={amenities}
                        activeAmenities={activeAmenities}
                        onChange={this._onAmenityChange}
                    />
                )}
                {isMonthly && (
                    <MonthlyFilters
                        amenities={amenities}
                        activeAmenities={activeAmenities}
                        activeReservationType={activeReservationType}
                        reservationTypes={reservationTypes}
                        onAmenityChange={this._onAmenityChange}
                        onReservationTypeChange={this._onReservationTypeChange}
                    />
                )}
                {priceHigh > 0 && priceLow > 0 && (
                    <PriceRange
                        low={priceLow}
                        high={highestPrice}
                        current={price ? price / 100 : priceHigh}
                        onAfterChange={this._onPriceRangeChange}
                    />
                )}
                {isMobile && (
                    <div className="SpotListFilters-controls">
                        <Button
                            className="SpotListFilters-close-button"
                            color="primary"
                            onClick={onCloseClick}
                            block
                        >
                            Done
                        </Button>
                        {isFiltered && (
                            <TextButton
                                className="SpotListFilters-reset-button"
                                onClick={this._onResetFiltersClick}
                            >
                                Reset All
                            </TextButton>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const mapStateToProps = state => {
        const {
            search: {
                data: {areFiltersVisible},
            },
            searchRequest: {monthly},
            filters: {numActive, activeAmenities, activeReservationType, price},
        } = state;
        const reservationTypes = getReservationTypes(state);
        let newActiveReservationType = activeReservationType;

        // check reservation type is valid
        if (
            newActiveReservationType !== 'all' &&
            isUndefined(reservationTypes[newActiveReservationType])
        ) {
            newActiveReservationType = 'all';
        }

        return {
            isMonthly: monthly,
            isVisible: areFiltersVisible,
            isFiltered: numActive > 0,
            amenities: getAmenities(state),
            activeAmenities,
            reservationTypes,
            activeReservationType: newActiveReservationType,
            price,
            ...getPriceRange(state),
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = {
    onCloseClick: toggleFiltersView,
    clearFilters: resetFilters,
    setFilters: updateFilters,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        makeMapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SpotListFilters);
